import {
  MoreVert as MoreIcon,
  NotificationsActiveOutlined as NotificationIcon,
} from "@mui/icons-material";
import {
  Autocomplete,
  Box,
  Checkbox,
  FormControlLabel,
  MenuItem,
  TextField,
  Typography,
} from "@mui/material";
import { DesktopDatePicker } from "@mui/x-date-pickers";
import { subMonths } from "date-fns";
import { useCallback, useContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAppDispatch, useAppSelector } from "../../../../app/hooks";
import AmountField from "../../../../common/components/functional/AmountField";
import { ActionsIconButton } from "../../../../common/components/styled/styled";
import { AutocompleteType } from "../../../../common/types/autoComplete";
import { Nullable } from "../../../../common/types/nullable";
import { useShortOrderReset } from "../../../../hooks/useShortOrderReset";
import { selectActiveWorkLocations } from "../../../customer/customerWorkLocationsSlice";
import { fetchOrderTemplates } from "../../../customer/orderTemplatesSlice";
import { AccessRightContext } from "../../../user/AccessRightProvider";
import Icons from "../../Icons";
import OrderActionsMenu from "../../OrderActionsMenu";
import OrderTemplateField from "../../OrderTemplateField";
import OrderTimeField from "../../OrderTimeField";
import { OrderFieldsProperties } from "../../additionalInfo/types";
import OrderFlexibleStartField from "../../components/OrderFlexibleStartField";
import { NEW_ORDER_COLUMN_WIDTHS } from "../../constants";
import { ShiftManualOfferDialog } from "../../dialogs/ShiftManualOfferDialog";
import {
  autoCompleteDefaultValue,
  Automatching,
  OrderFields,
} from "../../orderTypes";
import { OrderFormShortRowGridItem } from "../../styles";
import { TalentPool } from "../../types/common";
import { orderFieldTimePattern, validateFlexibleStartTime } from "../../utils";
import { ShiftOrderContext } from "./OrderShortDataProvider";

type Props = {
  rowId: string;
  values: OrderFields;
  isFirstRow?: boolean;
  bundleRowId?: string;
};

const OrderFormShortRowFields = ({
  rowId,
  bundleRowId,
  values,
  isFirstRow,
}: Props) => {
  const [hover, setHover] = useState(false);
  const [actionMenuAnchor, setActionMenuAnchor] = useState<null | HTMLElement>(
    null,
  );
  const [manualOfferDialogState, setManualOfferDialogState] = useState({
    open: false,
    id: "",
  });
  const [isFlexible, setIsFlexible] = useState(
    !!values.flexibleStartUntil || false,
  );

  const {
    bundling,
    setBundling,
    toggleRowBundling,
    handleChange,
    handleWorkLocationWithReset,
    handleError,
    validationErrors,
  } = useContext(ShiftOrderContext);
  const { isAdmin } = useContext(AccessRightContext);

  const { t } = useTranslation("order");
  const dispatch = useAppDispatch();

  const reasonForTemporaryEmploymentOptions = [
    { value: "AssignedWorkOrProject", label: "AssignedWorkOrProject" },
    { value: "ProductionPeak", label: "ProductionPeak" },
    { value: "Internship", label: "Internship" },
    { value: "Substitution", label: "Substitution" },
    { value: "SpecificOrder", label: "SpecificOrder" },
    { value: "Season", label: "Season" },
  ];

  const workLocationOptions = useAppSelector(selectActiveWorkLocations);
  const customerOrganization = useAppSelector(
    (state) => state.customerOrganization,
  );

  useShortOrderReset({
    values,
    bundleRowId,
    rowId,
    isFirstRow,
  });

  const handleManualOfferingChange = (
    automatching: Automatching,
    selectedPool: Nullable<TalentPool>,
  ) => {
    const talentsFromWorkLocationPool =
      selectedPool === TalentPool.WORK_LOCATION;

    const updatedAutomatching = {
      ...automatching,
      talentsFromWorkLocationPool,
      pool: selectedPool,
    };

    handleChange(
      "automatching",
      updatedAutomatching,
      rowId,
      values.id,
      isFirstRow,
    );
  };

  const handleWorkLocationChange = useCallback(
    (newWorkLocation: AutocompleteType | null) => {
      const workLocationValue = newWorkLocation || autoCompleteDefaultValue;

      handleWorkLocationWithReset(
        workLocationValue,
        rowId,
        values.id,
        isFirstRow,
      );
    },
    [handleWorkLocationWithReset, rowId, values.id, isFirstRow],
  );

  const workLocationCC =
    values.workLocation?.customerCCs?.map((cc) => ({
      id: cc.customerCCId,
      name: cc.name,
    })) || [];

  const costCenterOptions =
    workLocationCC.length > 0
      ? workLocationCC
      : customerOrganization.costCenters?.filter(
          (costCenter) => costCenter.active === true,
        ) || [];

  useEffect(() => {
    if (values.workLocation.contractId) {
      dispatch(
        fetchOrderTemplates({ contractId: values.workLocation.contractId }),
      );
    }
  }, [dispatch, values.workLocation.id, values.workLocation.contractId]);

  useEffect(() => {
    // update bundling state when the user changes orderTemplate, workLocation, amount or reasonForTemporaryEmployment
    // bundleTemplate can be changed only from bundling row
    if (bundling.show && bundleRowId === bundling.bundleId) {
      setBundling({
        ...bundling,
        bundleTemplate: values.orderTemplate.id,
        bundleWorkLocation: values.workLocation.id,
        bundleAmount: values.amount,
        bundleReasonForTemporaryEmployment: values.reasonForTemporaryEmployment,
      });
    }
    // eslint-disable-next-line
  }, [
    values.orderTemplate.id,
    values.workLocation.id,
    values.amount,
    values.reasonForTemporaryEmployment,
  ]);

  if (!values) return null;

  const disabled = !Boolean(values.workLocation.id && values.orderTemplate.id);
  const isHidden = !!bundleRowId && !isFirstRow; // show only date, time and customerCostCenter fields from the second bundled row
  const checked = bundling.show && !!bundleRowId; // bundled rows are checked

  // allow adding rows to bundle from only one order template and only with the same reason for temporary employment
  const allowToggleBundling =
    bundling.show &&
    !checked &&
    ((!!bundling.bundleTemplate &&
      values.orderTemplate.id !== bundling.bundleTemplate) ||
      (!!bundling.bundleWorkLocation &&
        values.workLocation.id !== bundling.bundleWorkLocation) ||
      (!!bundling.bundleReasonForTemporaryEmployment &&
        values.reasonForTemporaryEmployment !==
          bundling.bundleReasonForTemporaryEmployment) ||
      (!!bundling.bundleAmount && values.amount !== bundling.bundleAmount));

  // allow adding/remove rows to active bundle
  const allowModifyActiveBundle =
    bundling.show && !!bundleRowId && bundleRowId !== bundling.bundleId;

  const bundlingDisabled = allowToggleBundling || allowModifyActiveBundle;

  const hasTalents = !!values.automatching.talents.length;

  const showManuallyOfferedIcon =
    (bundleRowId && isFirstRow && hasTalents) || (!bundleRowId && hasTalents);
  const showNotificationIcon =
    (values.notifyWhenFulfilled ||
      !!values.notifyMinutesBeforeIfNotFulfilled) &&
    ((bundleRowId && isFirstRow) || !bundleRowId);
  const minDate = isAdmin ? undefined : subMonths(new Date(), 3);

  // wrapping call useChange callback to ease refactoring
  const handleChangeCallback = (
    name: OrderFieldsProperties,
    value:
      | AutocompleteType
      | null
      | number
      | string
      | Date
      | AutocompleteType[],
    isFirstRow?: boolean,
  ) => {
    handleChange(name, value, rowId, values.id, isFirstRow);
  };

  const handleFlexibleStartChange = (value: string) => {
    handleFlexibleStartError(value);
    handleChangeCallback("flexibleStartUntil", value);
  };

  const handleFlexibleStartError = (value: string) => {
    const validation = validateFlexibleStartTime(
      value,
      values.time,
      isFlexible,
    );

    handleError(!validation.isValid, {
      id: values.id,
      field: "flexibleStartUntil",
      errorType: validation.errorType || "invalidFlexibleStart",
    });
  };

  const handleFlexibleToggle = () => {
    const newIsFlexible = !isFlexible;
    setIsFlexible(newIsFlexible);

    if (!newIsFlexible) {
      // Reset flexibleStartUntil value and error when not flexible
      handleChangeCallback("flexibleStartUntil", "");
      handleError(false, {
        id: values.id,
        field: "flexibleStartUntil",
        errorType: "any",
      });
    } else {
      // Re-validate when enabling
      const validation = validateFlexibleStartTime(
        values.flexibleStartUntil || "",
        values.time,
        newIsFlexible,
      );

      handleError(!validation.isValid, {
        id: values.id,
        field: "flexibleStartUntil",
        errorType: validation.errorType || "invalidFlexibleStart",
      });
    }
  };

  const handleHover = (bool: boolean) => setHover(bool);

  const handleBundling = () =>
    toggleRowBundling(rowId, values.id, !!bundleRowId ? "remove" : "add");

  const handleToggleManualOfferDialog = (id?: string) => {
    if (id) {
      setManualOfferDialogState({ open: true, id });
      return setActionMenuAnchor(null);
    }

    setManualOfferDialogState({ open: false, id: "" });
  };

  const handleTimeError = (value: string) => {
    const timePattern = orderFieldTimePattern(value);
    const isValid =
      timePattern &&
      value.split("").every((char, index) => char.match(timePattern[index]));

    handleError(!isValid, {
      id: values.id,
      field: "time",
      errorType: "invalidTime",
    });
  };

  return (
    <>
      <Icons
        showManuallyOfferedIcon={showManuallyOfferedIcon}
        showBundling={bundling.show}
        handleHover={handleHover}
        handleBundling={handleBundling}
        bundlingDisabled={bundlingDisabled}
        hover={hover}
        checked={checked}
        values={values}
      />
      <OrderFormShortRowGridItem item xs>
        <Autocomplete
          sx={{
            backgroundColor: "rgba(249, 249, 250, 0.6)",
            visibility: isHidden ? "hidden" : "visible",
            "& :hover .MuiAutocomplete-input, & .Mui-focused .MuiAutocomplete-input":
              { minWidth: "30px" },
          }}
          disableClearable
          componentsProps={{ paper: { sx: { wordBreak: "break-all" } } }}
          value={values.workLocation}
          options={workLocationOptions.sort((a, b) =>
            a.name.localeCompare(b.name),
          )}
          getOptionLabel={(option) => option.name}
          renderInput={(params) => (
            <TextField {...params} size="small" name="workLocation" />
          )}
          onChange={(_, newValue: AutocompleteType | null) => {
            handleWorkLocationChange(newValue);
          }}
        />
      </OrderFormShortRowGridItem>
      <OrderTemplateField
        disabled={!values.workLocation.id}
        isHidden={isHidden}
        value={values.orderTemplate}
        onChange={handleChangeCallback}
        isFirstRow={isFirstRow}
      />
      <OrderFormShortRowGridItem item width={NEW_ORDER_COLUMN_WIDTHS.amount} xs>
        <AmountField
          isHidden={isHidden}
          fullWidth
          size="small"
          value={values.amount}
          disabled={disabled}
          onChange={(value: number) =>
            handleChangeCallback("amount", value, isFirstRow)
          }
          min={1}
          max={250}
        />
      </OrderFormShortRowGridItem>
      <OrderFormShortRowGridItem item width={NEW_ORDER_COLUMN_WIDTHS.date} xs>
        <Box
          display="flex"
          flexDirection="column"
          gap="6px"
          justifyContent="center"
        >
          <DesktopDatePicker
            // TODO: format EEEEEE dd.MM.yyyy (short weekday name + date) not supported/working in MUI V6
            format="dd.MM.yyyy"
            value={values.date}
            disabled={disabled}
            disableOpenPicker={disabled}
            minDate={minDate}
            onChange={(value) => handleChangeCallback("date", value)}
            onError={(reason) =>
              handleError(!!reason, {
                id: values.id,
                field: "date",
                errorType: "invalidDate",
              })
            }
            slotProps={{
              textField: {
                name: "date",
                size: "small",
                fullWidth: true,
                disabled: disabled,
                sx: {
                  backgroundColor: "rgba(249, 249, 250, 0.6)",
                },
              },
            }}
          />
          {/* Control for flexibleStartUntil field  */}
          {!bundleRowId && (
            <FormControlLabel
              sx={{ marginLeft: 0, marginRight: 0 }}
              onChange={handleFlexibleToggle}
              control={
                <Checkbox
                  sx={{ padding: "9px 4px 9px 0px" }}
                  disableRipple
                  checked={isFlexible}
                />
              }
              labelPlacement="end"
              label={
                <Typography variant="secondary" fontSize="14px">
                  {t("flexibleShiftStartLabel")}
                </Typography>
              }
            />
          )}
        </Box>
      </OrderFormShortRowGridItem>
      <OrderFormShortRowGridItem item width={NEW_ORDER_COLUMN_WIDTHS.time} xs>
        <Box display="flex" flexDirection="column" gap="8px">
          <OrderTimeField
            id={values.id}
            value={values.time}
            disabled={disabled}
            validationErrors={validationErrors}
            onChange={(value: string) => {
              handleTimeError(value);
              if (values.flexibleStartUntil) {
                // Re-validate flexibleStartUntil when time changes
                const validation = validateFlexibleStartTime(
                  values.flexibleStartUntil,
                  value,
                  isFlexible,
                );
                handleError(!validation.isValid, {
                  id: values.id,
                  field: "flexibleStartUntil",
                  errorType: validation.errorType || "invalidFlexibleStart",
                });
              }
              handleChangeCallback("time", value);
            }}
          />
          {/* flexibleStartUntil field */}
          {!bundleRowId && (
            <OrderFlexibleStartField
              value={values.flexibleStartUntil || ""}
              disabled={!isFlexible}
              onChange={(value: string) => handleFlexibleStartChange(value)}
              error={
                !!validationErrors.find(
                  (error) =>
                    error.id === values.id &&
                    error.field === "flexibleStartUntil",
                )
              }
            />
          )}
        </Box>
      </OrderFormShortRowGridItem>
      <OrderFormShortRowGridItem
        item
        width={NEW_ORDER_COLUMN_WIDTHS.customerCostCenter}
        xs
      >
        <Autocomplete
          value={values.customerCostCenter}
          sx={{ backgroundColor: "rgba(249, 249, 250, 0.6)" }}
          options={costCenterOptions.sort((a, b) =>
            a.name.localeCompare(b.name),
          )}
          disableClearable
          componentsProps={{ paper: { sx: { wordBreak: "break-all" } } }}
          getOptionLabel={(option) => option.name}
          disabled={disabled}
          renderInput={(params) => (
            <TextField {...params} size="small" name="customerCostCenter" />
          )}
          onChange={(_, newValue: AutocompleteType | null) => {
            handleChangeCallback(
              "customerCostCenter",
              newValue || autoCompleteDefaultValue,
            );
          }}
        />
      </OrderFormShortRowGridItem>
      <OrderFormShortRowGridItem
        item
        width={NEW_ORDER_COLUMN_WIDTHS.reasonForTemporaryEmployment}
        xs
      >
        <TextField
          select
          fullWidth
          sx={{
            backgroundColor: "rgba(249, 249, 250, 0.6)",
            visibility: isHidden ? "hidden" : "visible",
          }}
          size="small"
          name="reasonForTemporaryEmployment"
          value={values.reasonForTemporaryEmployment}
          disabled={disabled}
          onChange={(e) =>
            handleChangeCallback(
              "reasonForTemporaryEmployment",
              e.target.value,
              isFirstRow,
            )
          }
        >
          {reasonForTemporaryEmploymentOptions
            .sort((a, b) => a.label.localeCompare(b.label))
            .map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {t(option.label)}
              </MenuItem>
            ))}
        </TextField>
      </OrderFormShortRowGridItem>
      <OrderFormShortRowGridItem item width={NEW_ORDER_COLUMN_WIDTHS.more} xs>
        {!isHidden && (
          <ActionsIconButton
            size="small"
            onClick={(e) => setActionMenuAnchor(e.currentTarget)}
          >
            <MoreIcon />
          </ActionsIconButton>
        )}
      </OrderFormShortRowGridItem>
      <OrderActionsMenu
        rowId={rowId}
        id={values.id}
        values={values}
        open={!!actionMenuAnchor}
        anchorEl={actionMenuAnchor}
        onClose={() => setActionMenuAnchor(null)}
        bundleRowId={bundleRowId}
        additionalInfoDisabled={disabled}
        manuallyOffered={!!values.automatching.talents.length}
        handleToggleManualOfferDialog={(id) =>
          handleToggleManualOfferDialog(id)
        }
      />
      <OrderFormShortRowGridItem
        item
        width={NEW_ORDER_COLUMN_WIDTHS.notification}
      >
        {showNotificationIcon && (
          <Box height="40px" display="flex" alignItems="center">
            <NotificationIcon fontSize="small" />
          </Box>
        )}
      </OrderFormShortRowGridItem>
      {manualOfferDialogState.open && (
        <ShiftManualOfferDialog
          isOpen={manualOfferDialogState.open}
          closeDialog={handleToggleManualOfferDialog}
          workLocationId={values.workLocation.id}
          handleManualOfferingChange={handleManualOfferingChange}
          values={values}
          bundleId={bundleRowId}
        />
      )}
    </>
  );
};

export default OrderFormShortRowFields;
